import { Box, Typography, CircularProgress } from "@mui/material";
import { SpatialRun } from "src/pages/runs/types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "src/contexts/auth";
import { fetchWithToken } from "src/hooks/useFetch";
import FlowcellTileSelector from "./FlowcellTileSelector";
import { KIT_TYPE_TISSUE, WELL_LAYOUT_PANOS_SLIDE } from "./well-layout";
import {
  telemetryInstrumentFolder,
  telemetryLogsRootFrom,
} from "src/utils/telemetry-logs-path";

const bucket = "elembio-prod-usw2-s3-aviti-telemetry-logs-usw2";

interface CytoSegmentationThumbnailProps {
  run: SpatialRun;
}

export default function CytoSegmentationThumbnail({
  run,
}: CytoSegmentationThumbnailProps) {
  const { getAccessTokenSilently } = useAuth();
  const [selectedWell, setSelectedWell] = useState<string | null>(null);
  const [selectedTile, setSelectedTile] = useState<string | null>(null);
  const [thumbnailURL, setThumbnailURL] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const objectURLRef = useRef<string | null>(null);

  const {
    tenantID,
    instrumentID,
    instrumentSerialNumber,
    runID,
    wellLayout,
  } = run || {};

  // Build the base path for segmentation thumbnails
  const basePath = useMemo(() => {
    let tID = tenantID;
    if (tID === "") tID = "ten_traefikee";
    return `${bucket}/${telemetryLogsRootFrom(run)}/${tID}/${telemetryInstrumentFolder(instrumentID, instrumentSerialNumber)}${
      runID ? `/runs/${runID}/Thumbnails` : ""
    }`;
  }, [run, tenantID, instrumentID, instrumentSerialNumber, runID]);

  // Fetch thumbnail when tile is selected
  useEffect(() => {
    // Clean up previous object URL if any
    if (objectURLRef.current) {
      URL.revokeObjectURL(objectURLRef.current);
      objectURLRef.current = null;
    }

    if (!selectedTile || !selectedWell) {
      setThumbnailURL(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Path format: Well{WELL_NAME}/{Tile}_Segmentation_Thumbnail.tif
    const thumbnailPath = `${basePath}/Well${selectedWell}/${selectedTile}_Segmentation_Thumbnail.tif`;
    const payload = { path: thumbnailPath };

    // Call the new endpoint that returns PNG directly
    fetchWithToken(getAccessTokenSilently, "/segmentationThumbnail", {
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then((blob: Blob) => {
        // Create object URL from the blob
        const objectURL = URL.createObjectURL(blob);
        objectURLRef.current = objectURL;
        console.log("Segmentation thumbnail loaded:", objectURL);
        setThumbnailURL(objectURL);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching segmentation thumbnail:", err);
        setError("Failed to load thumbnail");
        setIsLoading(false);
      });

    // Cleanup function to revoke object URL when component unmounts or tile changes
    return () => {
      if (objectURLRef.current) {
        URL.revokeObjectURL(objectURLRef.current);
        objectURLRef.current = null;
      }
    };
  }, [selectedTile, selectedWell, basePath, getAccessTokenSilently]);

  const handleWellSelect = (wellLocation: string) => {
    setSelectedWell(wellLocation);
    setSelectedTile(null);
    setThumbnailURL(null);
  };

  const handleTileSelect = (tileName: string, wellLocation: string) => {
    setSelectedWell(wellLocation);
    setSelectedTile(tileName);
  };

  // Determine well layout string from run data
  // Values: "TwelveWellStandard", "OneWell", "FortyEightWell", "PanosSlide"
  const layoutType = useMemo(() => {
    if (wellLayout) return wellLayout;
    // Fallback: determine by number of wells. Only reached for runs with no
    // stored wellLayout. Tissue kits only run on single-well flow cells and
    // default to PanosSlide, so they must never fall through to the 12-well
    // default. Any other single-well run is a OneWell (Mega Well); PanosSlide
    // is not valid for non-tissue kits.
    const numWells = run?.wells?.length || 0;
    if (run?.kitType === KIT_TYPE_TISSUE) return WELL_LAYOUT_PANOS_SLIDE;
    if (numWells === 1) return "OneWell";
    if (numWells <= 12) return "TwelveWellStandard";
    return "FortyEightWell";
  }, [wellLayout, run?.wells?.length, run?.kitType]);

  return (
    <Box display="flex" gap={4} alignItems="flex-start">
      {/* Flowcell Tile Selector */}
      <FlowcellTileSelector
        wellLayout={layoutType}
        selectedWell={selectedWell}
        selectedTile={selectedTile}
        onWellSelect={handleWellSelect}
        onTileSelect={handleTileSelect}
      />

      {/* Thumbnail Display Area */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{ minWidth: 400 }}
      >
        {/* Title showing selected tile */}
        <Typography variant="h6" fontWeight="bold" pb={2}>
          {selectedWell && selectedTile
            ? `${selectedWell} - ${selectedTile}`
            : "Select a tile"}
        </Typography>

        {/* Thumbnail image */}
        <Box
          sx={{
            minHeight: 400,
            minWidth: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#000",
            borderRadius: 1,
          }}
        >
          {isLoading ? (
            <CircularProgress sx={{ color: "#fff" }} />
          ) : error ? (
            <Typography color="error" sx={{ color: "#ff6b6b" }}>
              {error}
            </Typography>
          ) : thumbnailURL ? (
            <img
              src={thumbnailURL}
              alt={`Segmentation thumbnail for ${selectedTile}`}
              style={{
                maxWidth: "100%",
                maxHeight: 600,
                objectFit: "contain",
                transform: "rotate(-90deg)",
              }}
            />
          ) : (
            <Typography sx={{ color: "#666" }}>
              {selectedTile
                ? "Loading..."
                : "Click on a tile to view its segmentation thumbnail"}
            </Typography>
          )}
        </Box>
        {thumbnailURL && (
          <Typography
            variant="caption"
            sx={{ mt: 2, color: "#666", fontStyle: "italic" }}
          >
            Image rotated 90° CCW to match flow cell layout
          </Typography>
        )}
      </Box>
    </Box>
  );
}
